import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Badge, Button, Card, Select, SelectItem,
  Tab, TabGroup, TabList, TabPanel, TabPanels,
  Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow,
  Text, TextInput, Textarea, Title,
} from '@tremor/react';
import { currentUser } from '../api/auth';
import {
  createReport, deleteReport, duplicateReport, fetchReports,
  previewReport, runReport, updateReport,
} from '../api/reports';
import {
  submitReportJob, listReportJobs, deleteReportJob, downloadReportJob,
} from '../api/reportjobs';
import type {
  Report, ReportDraft, ReportJob, ReportRunResult, ReportSource, User,
} from '../api/types';
import AccessDenied from '../components/AccessDenied';
import ColumnPicker from '../components/ColumnPicker';
import DrillCell from '../components/DrillCell';
import { refForCell } from '../lib/drilldown';
import Pagination from '../components/Pagination';
import ConfirmDialog from '../components/ConfirmDialog';
import Header from '../components/Header';
import PrintHeader from '../components/PrintHeader';
import SortEditor from '../components/SortEditor';
import TableSkeleton from '../components/TableSkeleton';
import { estimate } from '../lib/eta';
import { now } from '../lib/progress';
import Tour, { hasTourBeenSeen, markTourSeen } from '../components/Tour';
import { TOURS } from '../lib/tours';
import { fieldLabel, formatInt, formatMxn, rowsToCSV } from '../lib/format';
import { exportReportPdf } from '../lib/pdf';
import { t } from '../lib/i18n';
import { usePagination } from '../lib/usePagination';

export const SOURCES: ReportSource[] = ['sales', 'sales-lines', 'counter-lines', 'agent-sales', 'parts', 'ar', 'salespeople', 'facturasr', 'inventory', 'purchases', 'month-end'];

// Column catalog per source — drives the ColumnPicker / SortEditor chip
// lists. Hand-maintained alongside the daemon's emit shape; a missing entry
// just means a column won't be discoverable in the picker (the user can
// still hand-edit the raw key if they know it). Mirrors the i18n.fields
// keys so labels resolve automatically through fieldLabel().
export const AVAILABLE_COLUMNS: Record<ReportSource, string[]> = {
  sales: [
    'sid', 'tnum', 'anum', 'idate', 'itime_secs', 'slid',
    'subt_mxn', 'tax_mxn', 'total_mxn', 'type', 'paid_mxn', 'balance_mxn',
  ],
  parts: ['sid', 'line', 'part', 'pdesc', 'on_hand', 'min', 'last_sold', 'kind'],
  ar: ['anum', 'name', 'total_mxn'],
  salespeople: ['slid', 'name'],
  facturasr: [
    'sid', 'anum', 'name', 'rfc', 'inum', 'tnum', 'confnum',
    'idate', 'type', 'type_label', 'subt_total', 'iva', 'total', 'cost',
    'formapago', 'ctapago', 'slid', 'fecha_timbrado', 'factura_relacionada',
  ],
  inventory: [
    // Identifiers + description first so the default "no columns picked"
    // run is at least readable. Tail clusters by domain: stock/bin,
    // store pricing, store costing, factory pricing, factory costing,
    // catalog cross-refs, codes.
    'sid', 'line', 'part', 'pdesc',
    'on_hand', 'min_stock', 'bin', 'phydate',
    'price1_mxn', 'price2_mxn', 'price3_mxn', 'store_list_price',
    'cost_mxn', 'cost_usd',
    'factory_list_price', 'factory_price1', 'factory_price2', 'factory_price3',
    'factory_cost_mxn', 'factory_cost_usd', 'factory_core_cost',
    'sub_line', 'sub_part', 'sub_from_line', 'sub_from_part',
    'alt1_line', 'alt1_part', 'alt2_line', 'alt2_part',
    'part_comp', 'codes',
  ],
  purchases: [
    'cdate', 'days_open', 'sid', 'onum', 'sup_id', 'sup_name',
    'lines', 'qty_ord', 'qty_rcv', 'total_cost_mxn', 'status', 'last_recv_at',
  ],
  'sales-lines': [
    'idate', 'sid', 'tnum', 'anum', 'slid',
    'line', 'part', 'pdesc', 'qty_sold', 'sell_mxn', 'pcost_mxn', 'ext_mxn', 'type',
  ],
  'counter-lines': [
    'sid', 'ctrmn_id', 'ctrmn_name', 'line', 'part', 'qty_sold', 'sell_mxn',
    'importe_mxn', 'anum', 'prclev', 'cost1_mxn',
    'pdesc', 'idate', 'tnum', 'item_type', 'pcost_mxn',
  ],
  'agent-sales': [
    'sid', 'store_name', 'slid', 'slid_name', 'tickets',
    'venta_mxn', 'prev_venta_mxn', 'diff_mxn', 'diff_pct',
    'costo_mxn', 'margen_mxn', 'margen_pct', 'prev_from', 'prev_to',
  ],
  'month-end': [
    'sid', 'store_name', 'group_code', 'inv_cost_mxn', 'inv_snapshot_date',
    'xfer_out_mxn', 'xfer_in_mxn', 'purchases_mxn', 'cogs_header_mxn', 'cogs_lines_mxn',
    'sales_parts_mxn', 'sales_sin_iva_mxn', 'labor_mxn', 'labor_jobs_mxn', 'tickets', 'margin_pct',
  ],
};

type FilterField =
  | { key: string; label: string; kind: 'date' }
  | { key: string; label: string; kind: 'text'; placeholder?: string }
  | { key: string; label: string; kind: 'enum'; options: { value: string; label: string }[] };

const TYPE_FILTER_OPTS = [
  { value: 'all', label: 'Todos' },
  { value: 'cash', label: 'Contado' },
  { value: 'credit', label: 'Crédito' },
];
const PAID_FILTER_OPTS = [
  { value: 'all', label: 'Todos' },
  { value: 'paid', label: 'Pagados' },
  { value: 'unpaid', label: 'No pagados' },
];

export const FILTER_FIELDS: Record<ReportSource, FilterField[]> = {
  sales: [
    { key: 'from',        label: 'Desde',           kind: 'date' },
    { key: 'to',          label: 'Hasta',           kind: 'date' },
    { key: 'sid',         label: fieldLabel('sid') + ' (0=todas)', kind: 'text', placeholder: '0' },
    { key: 'slid',        label: fieldLabel('slid'), kind: 'text' },
    { key: 'type_filter', label: 'Tipo',             kind: 'enum', options: TYPE_FILTER_OPTS },
    { key: 'paid_filter', label: 'Estado de pago',   kind: 'enum', options: PAID_FILTER_OPTS },
  ],
  parts: [
    { key: 'search', label: 'Búsqueda parcial', kind: 'text', placeholder: 'A1234' },
    { key: 'kind',   label: 'Tipo',             kind: 'enum', options: [
      { value: 'all',  label: 'Todas' },
      { value: 'low',  label: 'Bajo mínimo' },
      { value: 'slow', label: 'Lento movimiento' },
    ]},
    { key: 'limit',  label: 'Límite', kind: 'text', placeholder: '200' },
  ],
  ar: [
    { key: 'sort',  label: 'Ordenar por', kind: 'enum', options: [
      { value: 'total',   label: 'Total' },
      { value: 'aging90', label: 'Antigüedad +90' },
      { value: 'name',    label: 'Nombre' },
    ]},
    { key: 'dir',   label: 'Dirección', kind: 'enum', options: [
      { value: 'asc',  label: 'Ascendente' },
      { value: 'desc', label: 'Descendente' },
    ]},
    { key: 'limit', label: 'Límite', kind: 'text', placeholder: '500' },
  ],
  salespeople: [],
  facturasr: [
    { key: 'from',        label: 'Desde',           kind: 'date' },
    { key: 'to',          label: 'Hasta',           kind: 'date' },
    { key: 'sid',         label: fieldLabel('sid') + ' (0=todas)', kind: 'text', placeholder: '0' },
    { key: 'type_filter', label: 'Tipo',             kind: 'enum', options: TYPE_FILTER_OPTS },
    { key: 'skip_stkxfr', label: 'Omitir transferencias', kind: 'enum', options: [
      { value: 'true',  label: 'Sí' },
      { value: 'false', label: 'No' },
    ]},
  ],
  inventory: [
    { key: 'sid',    label: fieldLabel('sid') + ' (0=todas)', kind: 'text', placeholder: '0' },
    { key: 'line',   label: 'Línea (prefijo)', kind: 'text', placeholder: '40' },
    { key: 'search', label: 'Parte contiene',  kind: 'text', placeholder: 'A1234' },
    { key: 'bin',    label: 'Bin (prefijo)',   kind: 'text', placeholder: 'A-01' },
    { key: 'has_stock', label: 'Con existencia', kind: 'enum', options: [
      { value: 'all', label: 'Todas' },
      { value: 'yes', label: 'Solo con stock' },
      { value: 'no',  label: 'Solo sin stock' },
    ]},
    { key: 'has_phydate', label: 'Inventario físico', kind: 'enum', options: [
      { value: 'all', label: 'Todas' },
      { value: 'yes', label: 'Solo escaneados' },
      { value: 'no',  label: 'Nunca escaneados' },
    ]},
    { key: 'limit', label: 'Límite', kind: 'text', placeholder: '5000 (máx 50000)' },
  ],
  purchases: [
    { key: 'from',   label: 'Desde', kind: 'date' },
    { key: 'to',     label: 'Hasta', kind: 'date' },
    { key: 'sid',    label: fieldLabel('sid') + ' (0=todas)', kind: 'text', placeholder: '0' },
    { key: 'sup_id', label: 'Proveedor (clave)', kind: 'text', placeholder: 'ACME' },
    { key: 'status', label: 'Estado', kind: 'enum', options: [
      { value: 'all',     label: 'Todas' },
      { value: 'open',    label: 'Sin recibir' },
      { value: 'partial', label: 'Parcial' },
      { value: 'closed',  label: 'Cerrada' },
    ]},
    { key: 'limit', label: 'Límite', kind: 'text', placeholder: '500' },
  ],
  'sales-lines': [
    { key: 'from',        label: 'Desde', kind: 'date' },
    { key: 'to',          label: 'Hasta', kind: 'date' },
    { key: 'sid',         label: fieldLabel('sid') + ' (0=todas)', kind: 'text', placeholder: '0' },
    { key: 'slid',        label: fieldLabel('slid'), kind: 'text' },
    { key: 'line',        label: 'Línea (prefijo)', kind: 'text', placeholder: '40' },
    { key: 'part',        label: 'Parte contiene',  kind: 'text', placeholder: 'A1234' },
    { key: 'type_filter', label: 'Tipo', kind: 'enum', options: TYPE_FILTER_OPTS },
    { key: 'limit',       label: 'Límite', kind: 'text', placeholder: '5000 (máx 50000)' },
  ],
  'counter-lines': [
    { key: 'from',         label: 'Desde', kind: 'date' },
    { key: 'to',           label: 'Hasta', kind: 'date' },
    { key: 'sid',          label: fieldLabel('sid') + ' (0=todas)', kind: 'text', placeholder: '0' },
    { key: 'lines',        label: 'Líneas (códigos exactos, separados por coma)', kind: 'text', placeholder: 'UCP,BAT' },
    { key: 'ctrmn_id',     label: fieldLabel('ctrmn_id') + ' (0=todos)', kind: 'text', placeholder: '0' },
    { key: 'exclude_code', label: 'Excluir código de inventario', kind: 'text', placeholder: 'NC' },
  ],
  'agent-sales': [
    { key: 'from',  label: 'Desde', kind: 'date' },
    { key: 'to',    label: 'Hasta', kind: 'date' },
    { key: 'sid',   label: fieldLabel('sid') + ' (0=todas)', kind: 'text', placeholder: '0' },
    { key: 'slids', label: 'Vendedores (ids, coma; vacío=todos)', kind: 'text', placeholder: '6,7,8' },
  ],
  'month-end': [
    { key: 'from',        label: 'Desde',           kind: 'date' },
    { key: 'to',          label: 'Hasta',           kind: 'date' },
    { key: 'sid',         label: fieldLabel('sid') + ' (0=todas)', kind: 'text', placeholder: '0' },
    { key: 'cost_basis',  label: 'Base de costo del inventario', kind: 'enum', options: [
      { value: 'avg',   label: 'Costo promedio (costave)' },
      { value: 'cost1', label: 'Costo 1 (cost1)' },
    ]},
    { key: 'inventory_as_of', label: 'Inventario al', kind: 'enum', options: [
      { value: 'range_end', label: 'Cierre del rango (día siguiente 00:05)' },
      { value: 'latest',    label: 'Último disponible' },
    ]},
  ],
};

// Date preset values — keep these in sync with the Go side's
// reports.ResolveDate vocabulary; any token not understood there returns
// an error from /run.
const DATE_PRESETS: { value: string; label: string }[] = [
  { value: '@today',            label: 'Hoy' },
  { value: '@yesterday',        label: 'Ayer' },
  { value: '@today_minus_7',    label: 'Hace 7 días' },
  { value: '@today_minus_30',   label: 'Hace 30 días' },
  { value: '@start_of_month',   label: 'Inicio del mes' },
  { value: '@end_of_month',     label: 'Fin del mes' },
  { value: '@last_month_start', label: 'Inicio del mes pasado' },
  { value: '@last_month_end',   label: 'Fin del mes pasado' },
  { value: '@start_of_year',    label: 'Inicio del año' },
  { value: '__custom__',        label: 'Personalizado…' },
];

const MXN_COLS = new Set([
  'subt_mxn', 'tax_mxn', 'total_mxn', 'paid_mxn', 'balance_mxn',
  'sell_mxn', 'pcost_mxn', 'extended_mxn', 'amount_mxn',
  'subt_total', 'iva', 'total', 'cost', 'total_cost_mxn', 'ext_mxn',
  // month-end (JCD-37)
  'inv_cost_mxn', 'xfer_out_mxn', 'xfer_in_mxn', 'purchases_mxn',
  'cogs_header_mxn', 'cogs_lines_mxn', 'sales_parts_mxn', 'sales_sin_iva_mxn',
  'labor_mxn', 'labor_jobs_mxn',
  // counter-lines (JCD-58)
  'importe_mxn', 'cost1_mxn',
  // agent-sales (JCD-76)
  'venta_mxn', 'prev_venta_mxn', 'diff_mxn', 'costo_mxn', 'margen_mxn',
]);
/**
 * Identifiers, not quantities — rendered bare. Ticket 4521 is "4521", never
 * "4,521": a thousands separator in a name is noise, and it reads especially
 * wrong now that these cells are drill-down affordances (JCD-59). The CSV
 * export has always written the raw value (rowsToCSV -> String(v)), so this
 * also puts the screen and the download back in agreement.
 */
const ID_COLS = new Set([
  'sid', 'tnum', 'anum', 'inum', 'onum',
  'ctrmn_id', 'prclev', // counter-lines (JCD-58): counterman id + price-level code
  'slid', // agent-sales (JCD-76): a salesperson id is text ("6", "ACO"), rendered bare and never grouped
]);

/** Real counts — these keep their grouping separators. */
const INT_COLS = new Set(['qty_sold', 'on_hand', 'min', 'type',
  'lines', 'qty_ord', 'qty_rcv', 'days_open',
  'tickets', // month-end (JCD-37)
]);

// Filter-map keys whose values are @-tokens or literal dates. Used to
// detect stale-results: any time one of these is a relative token AND the
// resolved date would now point at a different day than at runtime, we
// flag the result as potentially out-of-date.
const DATE_KEYS = new Set(['from', 'to', 'phydate_from', 'phydate_to']);

function emptyDraft(): ReportDraft {
  return {
    name: '',
    description: '',
    source: 'sales',
    filters: { from: '@start_of_month', to: '@today' },
    columns: [],
    sort: [],
    limit: 1000,
    shared: false,
  };
}

// Filters applied when the user picks a source in the editor's Datos tab
// (see the source <Select> in ReportEditor). Every source but month-end
// starts from a clean slate; month-end needs cost_basis/inventory_as_of
// populated (the Go provider defaults them too, but showing the resolved
// value up front avoids a "why is this enum blank" moment) and a
// last-month window, since a month-end close is normally run for the month
// that just ended, not the one in progress.
function defaultFiltersForSource(source: ReportSource): Record<string, unknown> {
  if (source === 'month-end') {
    return { from: '@last_month_start', to: '@last_month_end', cost_basis: 'avg', inventory_as_of: 'range_end' };
  }
  return {};
}

function downloadCsv(filename: string, result: ReportRunResult, columns?: string[]): void {
  if (result.rows.length === 0) return;
  // is_total is a row marker (month-end's TOTAL row, JCD-37), not a column —
  // exclude it from an inferred header the same way ResultsTable does.
  const cols = columns && columns.length > 0 ? columns : Object.keys(result.rows[0]).filter((c) => c !== 'is_total');
  const csv = rowsToCSV(result.rows, cols);
  const safe = filename.replace(/[^A-Za-z0-9_-]/g, '_') + '.csv';
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = safe;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// I5 (final review): the same sentence the on-screen TruncatedNotice banner
// shows, factored out so both the bespoke exportReportPdf meta and the
// browser's native print/save-as-PDF (via PrintHeader's subtitle) carry it
// from one place — and so it is unit-testable, since downloadReportPdf
// itself triggers a browser download.
export function truncatedMetaLine(result: ReportRunResult): string {
  return `${t.reports.truncated} ${formatInt(result.row_count)} ${t.reports.truncatedHint}`;
}

// Branded PDF of a report run. Columns/labels/cell formatting mirror the
// on-screen ResultsTable (fieldLabel + fmtCell + MXN/INT right-alignment).
// Capped at the same 1000 rows the table renders; CSV remains the full export.
function downloadReportPdf(
  name: string,
  result: ReportRunResult,
  columns: string[] | undefined,
  printedBy: string,
  metaLines: string[],
): void {
  if (result.rows.length === 0) return;
  // is_total is a row marker (month-end's TOTAL row, JCD-37), not a column —
  // exclude it from an inferred header the same way ResultsTable does.
  const cols = columns && columns.length > 0 ? columns : Object.keys(result.rows[0]).filter((c) => c !== 'is_total');
  const MAX = 1000;
  const numericColumns = cols.reduce<number[]>((acc, c, i) => {
    if (MXN_COLS.has(c) || INT_COLS.has(c) || ID_COLS.has(c)) acc.push(i);
    return acc;
  }, []);
  const meta = [...metaLines];
  // I5 (final review): this PDF already noted its own 1000-row render cap
  // below but silently dropped the daemon's own truncation — a report that
  // hit its row budget looked complete in the one artifact people actually
  // share.
  if (result.truncated) {
    meta.push(truncatedMetaLine(result));
  }
  if (result.rows.length > MAX) {
    meta.push(`Mostrando ${MAX} de ${result.rows.length} filas — usa CSV para el detalle completo.`);
  }
  exportReportPdf({
    title: name,
    meta,
    printedBy,
    columns: cols.map((c) => fieldLabel(c)),
    rows: result.rows.slice(0, MAX).map((r) => cols.map((c) => fmtCell(c, r[c]))),
    numericColumns,
    orientation: cols.length > 6 ? 'landscape' : 'portrait',
    filename: name.replace(/[^A-Za-z0-9_-]/g, '_') + '.pdf',
  });
}

export default function Reports() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    currentUser()
      .then((u) => { if (alive) setUser(u); })
      .catch(() => { if (alive) setUser(null); });
    return () => { alive = false; };
  }, []);
  if (user === undefined) return null;
  if (user === null) return <Navigate to="/login" replace />;
  if (user.role === 'staff') {
    return (
      <div className="min-h-screen flex flex-col">
        <Header user={user} />
        <main className="flex-1 p-4"><AccessDenied user={user} /></main>
      </div>
    );
  }
  return <ReportsInner user={user} />;
}

interface RunMeta {
  /** Display name for the results card header. */
  name: string;
  /** When the run completed. Drives the live-ticking "ejecutado hace N s". */
  ranAt: Date;
  /** Snapshot of the resolved date strings the daemon used. Comparing them
   *  to a re-resolution of the *current* filters detects stale results when
   *  a relative token like @today rolled over. */
  resolvedDates: Record<string, string>;
  /** The filter spec at run time — used to know which keys to re-resolve
   *  for staleness comparison. */
  filters: Record<string, unknown>;
}

function ReportsInner({ user }: { user: User }) {
  const [list, setList] = useState<Report[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [draft, setDraft] = useState<ReportDraft | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [result, setResult] = useState<ReportRunResult | null>(null);
  const [resultMeta, setResultMeta] = useState<RunMeta | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  // While a run/preview is in flight: name + start time + predicted ms (from
  // /api/eta history) so the results area shows a skeleton + elapsed/ETA instead
  // of leaving the previous result on screen. null when not running.
  const [running, setRunning] = useState<{ name: string; startedAt: number; etaMs: number | null } | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState<Report | null>(null);
  const [tourOpen, setTourOpen] = useState(false);
  const [jobs, setJobs] = useState<ReportJob[]>([]);
  const [jobNote, setJobNote] = useState<string | null>(null);
  useEffect(() => {
    if (!list) return;
    if (!hasTourBeenSeen(TOURS.reports.key)) {
      const id = setTimeout(() => setTourOpen(true), 600);
      return () => clearTimeout(id);
    }
  }, [list]);
  useEffect(() => {
    const onOpen = () => setTourOpen(true);
    window.addEventListener('pacebi:open-tour', onOpen);
    return () => window.removeEventListener('pacebi:open-tour', onOpen);
  }, []);
  // Forces a re-render every 10s so the "ejecutado hace N s" label keeps
  // pace with reality and the stale-results detector re-checks the clock.
  const [nowTick, setNowTick] = useState(0);

  function refresh() {
    setErr(null);
    fetchReports()
      .then(setList)
      .catch((e: Error) => setErr(e.message));
  }
  useEffect(refresh, []);

  // Job list + poll. Poll every 3s only while something is in flight, so an
  // idle Reports page makes no traffic.
  function loadJobs() { listReportJobs().then(setJobs).catch(() => { /* ignore */ }); }
  useEffect(loadJobs, []);
  const jobsInFlight = jobs.some((j) => j.status === 'queued' || j.status === 'running');
  useEffect(() => {
    if (!jobsInFlight) return;
    const id = window.setInterval(loadJobs, 3000);
    return () => window.clearInterval(id);
  }, [jobsInFlight]);

  async function submitAsync(def: ReportDraft | Report) {
    setErr(null);
    try {
      await submitReportJob(def as ReportDraft);
      setJobNote(t.reports.asyncSubmitted);
      loadJobs();
      window.setTimeout(() => setJobNote(null), 6000);
    } catch (e) {
      setErr((e as Error).message);
    }
  }
  async function removeJob(id: string) {
    try { await deleteReportJob(id); loadJobs(); }
    catch (e) { setErr((e as Error).message); }
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && draft && busy === null) {
        e.preventDefault();
        void preview();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, busy]);

  // Heartbeat for the relative-time label + staleness re-check. 10s feels
  // alive without burning CPU.
  useEffect(() => {
    if (!resultMeta) return;
    const id = window.setInterval(() => setNowTick((n) => n + 1), 10_000);
    return () => window.clearInterval(id);
  }, [resultMeta]);

  function startNew() {
    setDraft(emptyDraft());
    setEditingId(null);
    setResult(null);
    setResultMeta(null);
  }
  function startEdit(r: Report) {
    setDraft({
      name: r.name,
      description: r.description ?? '',
      source: r.source,
      filters: r.filters,
      columns: r.columns,
      sort: r.sort ?? [],
      limit: r.limit ?? 1000,
      shared: r.shared,
    });
    setEditingId(r.id);
    setResult(null);
    setResultMeta(null);
  }
  async function save() {
    if (!draft) return;
    setErr(null);
    try {
      if (editingId) await updateReport(editingId, draft);
      else await createReport(draft);
      setDraft(null);
      setEditingId(null);
      refresh();
    } catch (e) {
      setErr((e as Error).message);
    }
  }
  async function preview() {
    if (!draft) return;
    setErr(null);
    setBusy('preview');
    // Clear the stale result and show the running card (skeleton + elapsed/ETA)
    // so a slow run never leaves the PREVIOUS result on screen looking current.
    setResult(null);
    setResultMeta(null);
    setRunning({ name: draft.name || 'Vista previa', startedAt: now(), etaMs: estimate('POST', '/api/reports/run') });
    try {
      const r = await previewReport(draft);
      setResult(r);
      setResultMeta({
        name: draft.name || 'Vista previa',
        ranAt: new Date(),
        resolvedDates: pickDates(r.resolved_filters),
        filters: draft.filters,
      });
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
      setRunning(null);
    }
  }
  async function runSaved(r: Report) {
    setErr(null);
    setBusy('run:' + r.id);
    setResult(null);
    setResultMeta(null);
    setRunning({ name: r.name, startedAt: now(), etaMs: estimate('POST', '/api/reports/' + r.id + '/run') });
    try {
      const res = await runReport(r.id);
      setResult(res);
      setResultMeta({
        name: r.name,
        ranAt: new Date(),
        resolvedDates: pickDates(res.resolved_filters),
        filters: r.filters,
      });
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
      setRunning(null);
    }
  }
  async function csvFromRow(r: Report) {
    setErr(null);
    setBusy('csv:' + r.id);
    try {
      const res = await runReport(r.id);
      downloadCsv(r.name, res, r.columns);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  }
  async function destroyConfirmed(r: Report) {
    try {
      await deleteReport(r.id);
      refresh();
      if (editingId === r.id) {
        setDraft(null);
        setEditingId(null);
        setResult(null);
        setResultMeta(null);
      }
    } catch (e) {
      setErr((e as Error).message);
    }
  }
  async function duplicate(r: Report) {
    try {
      const copy = await duplicateReport(r.id);
      refresh();
      startEdit(copy);
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  const canEditRow = (r: Report) =>
    !r.builtin && (user.role === 'owner' || r.created_by === user.username);

  // Stale detection: re-resolve the report's date tokens against today's
  // clock. If any resolved value drifted from what the daemon recorded at
  // run time, the result is potentially out of date. Memo on the tick so
  // we don't recompute on every render.
  const isStale = useMemo(() => {
    if (!resultMeta) return false;
    const fresh = pickDates(resolveAllDateTokensLocally(resultMeta.filters));
    for (const k of Object.keys(resultMeta.resolvedDates)) {
      if (fresh[k] !== resultMeta.resolvedDates[k]) return true;
    }
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultMeta, nowTick]);

  function rerunCurrent() {
    if (!resultMeta) return;
    // Find the saved row by name match (resultMeta doesn't carry the id —
    // editingId is null for un-edited Run-from-list flow). Same lookup the
    // results-card CSV button uses.
    const ref = list?.find((x) => x.name === resultMeta.name);
    if (ref) void runSaved(ref);
    else if (draft) void preview();
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header user={user} />
      <main className="flex-1 px-6 py-6 space-y-4 max-w-screen-2xl mx-auto w-full">
        <div className="flex items-baseline gap-3">
          <Title>{t.reports.title}</Title>
          <Text className="text-tremor-content text-sm">{t.reports.subtitle}</Text>
          <div className="ml-auto flex gap-2">
            <Button
              variant="secondary"
              onClick={() => window.dispatchEvent(new CustomEvent('pacebi:support-open', {
                detail: { tab: 'support', preset: { kind: 'suggestion', title: t.reports.requestReportTitlePrefix, intro: t.reports.requestReportIntro } },
              }))}
            >
              {t.reports.requestReport}
            </Button>
            <Button onClick={startNew} data-tour="reports-new">{t.reports.new}</Button>
          </div>
        </div>

        {err && <Card><Text className="text-red-600 dark:text-red-400">{err}</Text></Card>}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* List */}
          <Card className="lg:col-span-1 p-0 overflow-hidden" data-tour="reports-list">
            {!list && <div className="p-4"><Text>{t.common.loading}</Text></div>}
            {list && list.length === 0 && <div className="p-4"><Text>{t.reports.empty}</Text></div>}
            {list && list.length > 0 && (
              <ul className="divide-y divide-tremor-border">
                {list.map((r) => (
                  <ReportRow
                    key={r.id}
                    report={r}
                    busy={busy}
                    canEdit={canEditRow(r)}
                    onRun={() => runSaved(r)}
                    onCsv={() => csvFromRow(r)}
                    onAsync={() => submitAsync(r)}
                    onDuplicate={() => duplicate(r)}
                    onEdit={() => startEdit(r)}
                    onDelete={() => setConfirmingDelete(r)}
                  />
                ))}
              </ul>
            )}
          </Card>

          {/* Editor + results */}
          <div className="lg:col-span-2 space-y-4">
            {draft && (
              <Card>
                <Title>{editingId ? t.reports.edit : t.reports.new}</Title>
                <ReportEditor draft={draft} onChange={setDraft} />
                <div className="mt-4 flex gap-2 items-center">
                  <Button onClick={save} disabled={!draft.name.trim()}>{t.reports.save}</Button>
                  <Button
                    variant="secondary"
                    loading={busy === 'preview'}
                    disabled={busy !== null}
                    onClick={preview}
                  >
                    {t.reports.runPreview}
                  </Button>
                  <Button
                    variant="light"
                    disabled={!draft.name.trim()}
                    onClick={() => submitAsync(draft)}
                    title={t.reports.asyncHint}
                  >
                    {t.reports.async}
                  </Button>
                  <Button variant="light" onClick={() => { setDraft(null); setEditingId(null); }}>
                    {t.reports.cancel}
                  </Button>
                  <Text className="text-tremor-content text-xs ml-auto">⌘/Ctrl+Enter para ejecutar</Text>
                </div>
              </Card>
            )}

            {running && !result && (
              <Card data-testid="report-running">
                <div className="flex items-baseline gap-3 flex-wrap">
                  <Title>{running.name}</Title>
                  <Text className="text-tremor-content text-sm">{t.progress.running}</Text>
                  <RunClock startedAt={running.startedAt} etaMs={running.etaMs} />
                </div>
                <div className="mt-4">
                  <TableSkeleton rows={6} cols={5} />
                </div>
              </Card>
            )}

            {result && resultMeta && (
              <div className="print-region">
                <Card>
                  <div className="flex items-baseline gap-3 flex-wrap no-print">
                    <Title>{resultMeta.name}</Title>
                    <Text className="text-tremor-content text-sm">
                      {result.row_count} {t.reports.rowCount}
                    </Text>
                    <Text className="text-tremor-content text-xs">
                      · ejecutado {formatRelative(resultMeta.ranAt)}
                    </Text>
                    {isStale && (
                      <Badge color="amber">
                        Datos posiblemente desactualizados
                      </Badge>
                    )}
                    {result.truncated && <TruncatedNotice count={result.row_count} />}
                    <div className="ml-auto flex gap-2">
                      {isStale && (
                        <Button size="xs" variant="primary" onClick={rerunCurrent}>
                          Re-ejecutar
                        </Button>
                      )}
                      <Button
                        size="xs"
                        variant="secondary"
                        onClick={() => {
                          const ref = list?.find((x) => x.id === editingId);
                          const cols = ref?.columns?.length ? ref.columns : undefined;
                          downloadCsv(resultMeta.name, result, cols);
                        }}
                      >
                        {t.reports.downloadCsv}
                      </Button>
                      <Button
                        size="xs"
                        variant="secondary"
                        onClick={() => {
                          const ref = list?.find((x) => x.id === editingId);
                          const cols = ref?.columns?.length ? ref.columns : undefined;
                          downloadReportPdf(
                            resultMeta.name, result, cols, user.username,
                            [`${result.row_count} ${t.reports.rowCount}`],
                          );
                        }}
                      >
                        {t.reports.downloadPdf}
                      </Button>
                    </div>
                  </div>
                  {/* I5 (final review): TruncatedNotice above lives in the
                      no-print toolbar, so a browser print / save-as-PDF of
                      this page never showed it — .print-only is otherwise
                      only ever driven through PrintHeader's subtitle in this
                      app, so the truncation sentence rides along there. */}
                  <PrintHeader
                    title={resultMeta.name}
                    subtitle={result.truncated ? truncatedMetaLine(result) : undefined}
                  />
                  <ResultsTable rows={result.rows} />
                </Card>
              </div>
            )}

            {!draft && !result && (
              <Card>
                <div className="text-center py-8 space-y-2">
                  <Text className="text-tremor-content-strong">
                    Selecciona un reporte de la lista y haz clic en <strong>{t.reports.run}</strong>.
                  </Text>
                  <Text className="text-tremor-content text-sm">
                    O usa <strong>{t.reports.new}</strong> para crear uno desde cero, o <strong>{t.reports.duplicate}</strong> sobre un reporte predefinido para hacer tu propia versión.
                  </Text>
                </div>
              </Card>
            )}
          </div>
        </div>

        {jobNote && (
          <Card className="bg-emerald-50 border-emerald-200 dark:bg-emerald-950 dark:border-emerald-900">
            <Text className="text-emerald-800 dark:text-emerald-200 text-sm">{jobNote}</Text>
          </Card>
        )}

        <Card>
          <Title className="text-base">{t.reports.jobsTitle}</Title>
          {jobs.length === 0 ? (
            <Text className="text-tremor-content text-sm mt-2">{t.reports.jobsEmpty}</Text>
          ) : (
            <Table className="mt-2">
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{t.reports.jobCols.name}</TableHeaderCell>
                  <TableHeaderCell>{t.reports.jobCols.status}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{t.reports.jobCols.rows}</TableHeaderCell>
                  <TableHeaderCell>{t.reports.jobCols.created}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{t.reports.jobCols.actions}</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {jobs.map((j) => (
                  <TableRow key={j.id}>
                    <TableCell className="whitespace-nowrap">{j.name}</TableCell>
                    <TableCell>
                      <Badge color={
                        j.status === 'done' ? 'emerald'
                        : j.status === 'failed' ? 'red'
                        : j.status === 'running' ? 'blue' : 'gray'
                      }>
                        {t.reports.jobStatus[j.status]}
                      </Badge>
                      {j.status === 'failed' && j.error && (
                        <span className="ml-2 text-xs text-red-600 dark:text-red-400" title={j.error}>·</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {j.status === 'done' ? formatInt(j.row_count) : '—'}
                      {j.status === 'done' && j.truncated && (
                        <span className="ml-1 text-xs text-red-600 dark:text-red-400" title={t.reports.truncatedHint}>{t.reports.truncatedShort}</span>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-tremor-content text-xs">
                      {formatRelative(new Date(j.created_at))}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {j.status === 'done' && (
                        <Button size="xs" variant="secondary" onClick={() => downloadReportJob(j.id)}>
                          {t.reports.downloadCsv}
                        </Button>
                      )}
                      <Button size="xs" variant="light" className="ml-2" onClick={() => removeJob(j.id)}>
                        ×
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </main>

      {confirmingDelete && (
        <ConfirmDialog
          title={t.reports.delete}
          message={t.reports.confirmDelete + ' "' + confirmingDelete.name + '"'}
          confirmLabel={t.reports.delete}
          cancelLabel={t.reports.cancel}
          onConfirm={() => {
            void destroyConfirmed(confirmingDelete);
            setConfirmingDelete(null);
          }}
          onCancel={() => setConfirmingDelete(null)}
        />
      )}
      {tourOpen && (
        <Tour
          tourKey={TOURS.reports.key}
          steps={TOURS.reports.steps}
          onClose={() => { setTourOpen(false); markTourSeen(TOURS.reports.key); }}
        />
      )}
    </div>
  );
}

function ReportRow({
  report, busy, canEdit, onRun, onCsv, onAsync, onDuplicate, onEdit, onDelete,
}: {
  report: Report;
  busy: string | null;
  canEdit: boolean;
  onRun: () => void;
  onCsv: () => void;
  onAsync: () => void;
  onDuplicate: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const r = report;
  const isRunning = busy === 'run:' + r.id;
  const isCsving  = busy === 'csv:' + r.id;
  const anyBusy   = busy !== null;
  return (
    <li className="px-3 py-3 hover:bg-tremor-background-muted">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-semibold flex items-center gap-2 flex-wrap">
            <span className="truncate">{r.name}</span>
            {r.builtin && <Badge color="violet">{t.reports.builtin}</Badge>}
            {r.shared && !r.builtin && <Badge color="blue">Compartido</Badge>}
          </div>
          <div className="text-tremor-content text-xs">
            {t.reports.source[r.source]} · {r.created_by}
          </div>
          {r.description && (
            <div
              className="text-tremor-content text-xs mt-1 line-clamp-2"
              title={r.description}
            >
              {r.description}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1 items-stretch">
          <Button size="xs" variant="primary" loading={isRunning} disabled={anyBusy} onClick={onRun}>
            {t.reports.run}
          </Button>
          <Button size="xs" variant="secondary" loading={isCsving} disabled={anyBusy} onClick={onCsv}>
            CSV
          </Button>
          <Button size="xs" variant="light" disabled={anyBusy} onClick={onAsync} title={t.reports.asyncHint}>
            {t.reports.asyncShort}
          </Button>
          <div className="flex gap-1">
            <Button size="xs" variant="light" disabled={anyBusy} onClick={onDuplicate}>
              {t.reports.duplicate}
            </Button>
            {canEdit && (
              <>
                <Button size="xs" variant="light" disabled={anyBusy} onClick={onEdit}>
                  {t.reports.edit}
                </Button>
                <Button size="xs" variant="light" color="red" disabled={anyBusy} onClick={onDelete}>
                  ×
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </li>
  );
}

function ReportEditor({
  draft, onChange,
}: { draft: ReportDraft; onChange: (d: ReportDraft) => void }) {
  const fields = FILTER_FIELDS[draft.source];
  const available = AVAILABLE_COLUMNS[draft.source] ?? [];
  function setFilter(k: string, v: unknown) {
    const next = { ...draft.filters };
    if (v === '' || v === undefined || v === null) delete next[k];
    else next[k] = v;
    onChange({ ...draft, filters: next });
  }
  return (
    <TabGroup className="mt-3">
      <TabList>
        <Tab>Datos</Tab>
        <Tab>Filtros</Tab>
        <Tab>Salida</Tab>
      </TabList>
      <TabPanels>
        {/* DATOS */}
        <TabPanel>
          <div className="space-y-3 mt-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label={t.reports.field.name}>
                <TextInput value={draft.name} onChange={(e) => onChange({ ...draft, name: e.target.value })} />
              </Field>
              <Field label={t.reports.field.source}>
                <Select
                  value={draft.source}
                  onValueChange={(v) => onChange({ ...draft, source: v as ReportSource, filters: defaultFiltersForSource(v as ReportSource), columns: [], sort: [] })}
                >
                  {SOURCES.map((s) => (
                    <SelectItem key={s} value={s}>{t.reports.source[s]}</SelectItem>
                  ))}
                </Select>
              </Field>
            </div>
            <Field label={t.reports.field.description}>
              <Textarea
                value={draft.description ?? ''}
                rows={2}
                onChange={(e) => onChange({ ...draft, description: e.target.value })}
              />
            </Field>
            <Field label={t.reports.field.shared}>
              <Select
                value={draft.shared ? 'yes' : 'no'}
                onValueChange={(v) => onChange({ ...draft, shared: v === 'yes' })}
              >
                <SelectItem value="no">Privado (solo tú)</SelectItem>
                <SelectItem value="yes">Compartido con el equipo</SelectItem>
              </Select>
            </Field>
          </div>
        </TabPanel>

        {/* FILTROS */}
        <TabPanel>
          {fields.length === 0 ? (
            <Text className="mt-3 text-tremor-content text-sm">
              Esta fuente no admite filtros configurables.
            </Text>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              {fields.map((f) => (
                <div key={f.key}>
                  <Text className="text-tremor-content text-xs">{f.label}</Text>
                  {f.kind === 'date' && (
                    <DateField
                      value={(draft.filters[f.key] as string) ?? ''}
                      onChange={(v) => setFilter(f.key, v)}
                    />
                  )}
                  {f.kind === 'enum' && (
                    <Select
                      value={(draft.filters[f.key] as string) ?? ''}
                      onValueChange={(v) => setFilter(f.key, v)}
                    >
                      {f.options.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </Select>
                  )}
                  {f.kind === 'text' && (
                    <TextInput
                      placeholder={f.placeholder}
                      value={(draft.filters[f.key] as string) ?? ''}
                      onChange={(e) => setFilter(f.key, e.target.value)}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
          <Text className="text-tremor-content text-xs mt-3">{t.reports.dateTokenHelp}</Text>
        </TabPanel>

        {/* SALIDA */}
        <TabPanel>
          <div className="space-y-4 mt-3">
            <Field label={t.reports.field.columns}>
              <ColumnPicker
                available={available}
                value={draft.columns}
                onChange={(cols) => onChange({ ...draft, columns: cols })}
              />
            </Field>
            <Field label={t.reports.field.sort}>
              <SortEditor
                available={available}
                value={draft.sort ?? []}
                onChange={(s) => onChange({ ...draft, sort: s })}
              />
            </Field>
            <Field label={t.reports.field.limit}>
              <TextInput
                value={String(draft.limit ?? 0)}
                onChange={(e) => onChange({ ...draft, limit: Number(e.target.value) || 0 })}
                placeholder="1000  (0 = sin límite, hasta 50000)"
              />
            </Field>
          </div>
        </TabPanel>
      </TabPanels>
    </TabGroup>
  );
}

function DateField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const isCustom = value !== '' && !value.startsWith('@');
  const selectValue = isCustom ? '__custom__' : value;
  return (
    <div className="space-y-1">
      <Select value={selectValue} onValueChange={(v) => onChange(v === '__custom__' ? '' : v)}>
        {DATE_PRESETS.map((p) => (
          <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
        ))}
      </Select>
      {isCustom && (
        <TextInput placeholder="YYYY-MM-DD" value={value} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}

/** JCD-58 — a run that hit its row budget is never left looking complete. */
export function TruncatedNotice({ count }: { count: number }) {
  return (
    <div role="status" className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
      {t.reports.truncated} {formatInt(count)} {t.reports.truncatedHint}
    </div>
  );
}

// Exported for the pagination test; only Reports renders it in the app.
export function ResultsTable({ rows }: { rows: Record<string, unknown>[] }) {
  // Client-side pager over the full result (was a hard 1000-row cut). CSV
  // still exports everything; the PDF keeps its own cap + note.
  const pg = usePagination(rows);
  // The table lives in its own scroll box — a page change should start at the top.
  const boxRef = useRef<HTMLDivElement>(null);
  useEffect(() => { if (boxRef.current) boxRef.current.scrollTop = 0; }, [pg.page]);
  if (rows.length === 0) {
    return <Text className="mt-3">{t.reports.noRows}</Text>;
  }
  // is_total is a row marker (month-end's TOTAL row, JCD-37), not a column —
  // exclude it even if it ever ended up on the row used to derive cols.
  const cols = Object.keys(rows[0]).filter((c) => c !== 'is_total');
  return (
    // overflow-x-auto + whitespace-nowrap on cells = the table grows past the
    // container width when there are many columns, triggering horizontal
    // scroll. overflow-y-auto + maxHeight + sticky thead handles vertical.
    <>
    <div
      ref={boxRef}
      className="mt-3 overflow-x-auto overflow-y-auto print-expand border border-tremor-border rounded-tremor-default"
      style={{ maxHeight: '60vh' }}
    >
      <Table>
        <TableHead className="sticky top-0 bg-tremor-background z-10 shadow-sm">
          <TableRow>
            {cols.map((c) => (
              <TableHeaderCell
                key={c}
                title={c}
                className={
                  'whitespace-nowrap' +
                  (MXN_COLS.has(c) || INT_COLS.has(c) || ID_COLS.has(c) ? ' text-right' : '')
                }
              >
                {fieldLabel(c)}
              </TableHeaderCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {pg.pageRows.map((r, i) => (
            <TableRow key={pg.from + i} className={r.is_total ? 'font-semibold' : undefined}>
              {cols.map((c) => (
                <TableCell
                  key={c}
                  className={
                    'whitespace-nowrap' +
                    (MXN_COLS.has(c) || INT_COLS.has(c) || ID_COLS.has(c) ? ' text-right tabular-nums' : '')
                  }
                >
                  {/* JCD-59: an identifier cell opens its record in a modal.
                      refForCell returns null when the row lacks the companion
                      fields (a sid for a ticket, a line for a part), and
                      DrillCell then renders plain text — no dead-end clicks. */}
                  <DrillCell target={refForCell(c, r)}>{fmtCell(c, r[c])}</DrillCell>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
    <Pagination {...pg} />
    </>
  );
}

function fmtCell(col: string, v: unknown): string {
  if (v == null || v === '') return '';
  if (typeof v === 'object') return JSON.stringify(v);
  if (typeof v === 'number') {
    if (MXN_COLS.has(col)) return formatMxn(v);
    if (ID_COLS.has(col)) return String(v);
    // I7 (final review): INT_COLS are "real counts" that keep their grouping
    // separator, but a non-integer quantity (a fractional qty_sold is valid
    // data, e.g. bulk items sold by partial unit) must not get rounded away
    // on screen/PDF while the CSV export (rowsToCSV -> String(v)) writes the
    // exact value — 1.5 silently became "2" here before.
    if (INT_COLS.has(col)) return Number.isInteger(v) ? formatInt(v) : v.toLocaleString('es-MX', { maximumFractionDigits: 2 });
    return Number.isInteger(v) ? String(v) : v.toFixed(2);
  }
  if (typeof v === 'string') return v;
  return String(v);
}

function formatRelative(when: Date): string {
  const seconds = Math.max(0, Math.floor((Date.now() - when.getTime()) / 1000));
  if (seconds < 5)   return 'justo ahora';
  if (seconds < 60)  return `hace ${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60)  return `hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24)    return `hace ${hours} h`;
  return when.toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' });
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <Text className="text-tremor-content text-xs mb-1">{label}</Text>
      {children}
    </div>
  );
}

// pickDates returns only the date-shaped entries from a resolved-filters
// map. The result is compared against a fresh local resolution to flag
// stale results when @today / @end_of_month / etc rolled over.
function pickDates(resolved: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const k of Object.keys(resolved)) {
    if (!DATE_KEYS.has(k)) continue;
    const v = resolved[k];
    if (typeof v === 'string') out[k] = v;
  }
  return out;
}

// resolveAllDateTokensLocally mirrors reports/dates.go::ResolveDate. Used
// only for stale-results detection — the daemon does the authoritative
// resolution at run time, this is a heuristic so the SPA can show a badge
// without round-tripping. Keep in sync with the Go vocabulary.
function resolveAllDateTokensLocally(filters: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const k of Object.keys(filters)) {
    if (!DATE_KEYS.has(k)) continue;
    const v = filters[k];
    if (typeof v !== 'string') continue;
    out[k] = resolveToken(v);
  }
  return out;
}

function resolveToken(token: string): string {
  if (!token.startsWith('@')) return token; // literal date, passes through
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth(); // 0-indexed
  const d = now.getDate();
  const ymd = (yy: number, mm: number, dd: number): string => {
    const dt = new Date(yy, mm, dd);
    return dt.getFullYear() + '-' + pad2(dt.getMonth() + 1) + '-' + pad2(dt.getDate());
  };
  if (token === '@today')            return ymd(y, m, d);
  if (token === '@yesterday')        return ymd(y, m, d - 1);
  if (token === '@start_of_month')   return ymd(y, m, 1);
  if (token === '@end_of_month')     return ymd(y, m + 1, 0);     // day 0 = last day of prev month
  if (token === '@last_month_start') return ymd(y, m - 1, 1);
  if (token === '@last_month_end')   return ymd(y, m, 0);
  if (token === '@start_of_year')    return ymd(y, 0, 1);
  const mLb = /^@today_minus_(\d+)$/.exec(token);
  if (mLb) return ymd(y, m, d - parseInt(mLb[1], 10));
  return token; // unknown — let the server complain
}

function pad2(n: number): string { return String(n).padStart(2, '0'); }

/**
 * Live elapsed-seconds counter shown while a report runs, plus a deterministic
 * "≈ N s restante" when /api/eta has timing history for this run. `startedAt`
 * uses the same monotonic clock as lib/progress.now().
 */
function RunClock({ startedAt, etaMs }: { startedAt: number; etaMs: number | null }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((n) => n + 1), 500);
    return () => window.clearInterval(id);
  }, []);
  const elapsedMs = Math.max(0, now() - startedAt);
  let eta: string | null = null;
  if (etaMs != null) {
    const remaining = etaMs - elapsedMs;
    eta = remaining > 0 ? `≈ ${Math.ceil(remaining / 1000)} s ${t.progress.etaSuffix}` : t.progress.almostDone;
  }
  return (
    <Text className="text-tremor-content text-xs tabular-nums">
      {Math.floor(elapsedMs / 1000)} s{eta ? ` · ${eta}` : ''}
    </Text>
  );
}
